import { createLogic } from 'redux-logic';
import {
  OrderLineItemDiscount,
  order as orderApi,
} from '@squareup/sq-addons-web-sdk';

import { AppState } from 'store/store';
import {
  ADD_DISCOUNT,
  CLEAR_DISCOUNTS,
  FETCH_DISCOUNTS,
  REMOVE_DISCOUNT,
  REMOVE_DISCOUNTS,
  AddDiscountAction,
  RemoveDiscountAction,
  RemoveDiscountsAction,
} from './types';
import {
  addDiscount,
  addDiscountFulfilled,
  addDiscountRejected,
  clearDiscountsFulfilled,
  clearDiscountsRejected,
  fetchDiscountsFulfilled,
  fetchDiscountsRejected,
  removeDiscountFulfilled,
  removeDiscountRejected,
  removeDiscountsFulfilled,
  removeDiscountsRejected,
} from './actions';
import { deleteReward } from 'store/api/discounts/deleteReward';
import { getRewards } from 'lava/api';
import { addCustomerToSale } from 'store/customer';

export const addDiscountLogic = createLogic({
  type: ADD_DISCOUNT,
  cancelType: CLEAR_DISCOUNTS,

  async process(
    {
      action,
      getState,
      cancelled$,
    }: {
      action: AddDiscountAction;
      getState: () => object;
      cancelled$: { subscribe: (cb: () => void) => void };
    },
    dispatch,
    done
  ) {
    let cancelled = false;
    let discountId = '';

    cancelled$.subscribe(async () => {
      cancelled = true;
    });

    const posContext = (getState() as AppState).context.posContext;
    const customer = (getState() as AppState).customer.customer;

    const discount = action.payload.discount;

    if (customer) {
      try {
        // // Create the reward
        // const rewardId = await createReward(
        //   posContext,
        //   customer.referenceId,
        //   discount.metadata.rewardTierId
        // );
        // discount.metadata.rewardId = rewardId;

        // Add the discount to the order.
        discountId = await orderApi.addDiscount(discount);
        const updatedOrder = await orderApi.retrieve();

        // const account = await retrieveLoyaltyAccount(
        //   posContext,
        //   customer.referenceId
        // );
        //
        // Refresh the rewards
        const rewards = await getRewards(
          posContext,
          customer.referenceId,
          updatedOrder
        );

        if (!cancelled) {
          // Update discounts in redux.
          dispatch(
            fetchDiscountsFulfilled(
              rewards.availableDiscounts,
              rewards.appliedDiscounts,
              rewards.unavailableDiscounts,
              rewards.updatedDiscounts
            )
          );

          dispatch(addDiscountFulfilled());
        }
      } catch (error) {
        // Remove discount from order if it was added.
        if (discountId) {
          await orderApi.removeDiscount(discountId);
        }

        dispatch(addDiscountRejected(error as Error));
      } finally {
        done();
      }
    } else {
      dispatch(addDiscountRejected(new Error('Customer is not defined')));
    }
  },
});

export const clearDiscountsLogic = createLogic({
  type: CLEAR_DISCOUNTS,

  async process({ getState }: { getState: () => object }, dispatch, done) {
    const posContext = (getState() as AppState).context.posContext;
    const customer = (getState() as AppState).customer.customer;

    if (customer) {
      try {
        const order = await orderApi.retrieve();
        const addonDiscounts = (order.discounts ?? []).filter(
          (discount: OrderLineItemDiscount) => !!discount.metadata?.rewardId
        );

        // Remove all Square Loyalty Addon discounts (because Square Point of
        // Sale Discounts could also be present).
        for (const discount of addonDiscounts) {
          if (discount.uid) {
            await orderApi.removeDiscount(discount.uid);
            await deleteReward(
              posContext,
              discount.metadata?.rewardId as string
            );
          }
        }

        const updatedOrder = await orderApi.retrieve();

        const rewards = await getRewards(
          posContext,
          customer.referenceId,
          updatedOrder
        );

        // Update discounts in redux.
        dispatch(
          fetchDiscountsFulfilled(
            rewards.availableDiscounts,
            rewards.appliedDiscounts,
            rewards.unavailableDiscounts,
            rewards.updatedDiscounts
          )
        );

        dispatch(clearDiscountsFulfilled());
      } catch (error) {
        dispatch(clearDiscountsRejected(error as Error));
      } finally {
        done();
      }
    } else {
      dispatch(clearDiscountsRejected(new Error('Customer is not defined')));
    }
  },
});

export const fetchDiscountsLogic = createLogic({
  type: FETCH_DISCOUNTS,

  async process({ getState }: { getState: () => object }, dispatch, done) {
    const posContext = (getState() as AppState).context.posContext;
    const customer = (getState() as AppState).customer?.customer;

    const order = await orderApi.retrieve();

    if (order && customer) {
      try {
        const rewards = await getRewards(
          posContext,
          customer.referenceId,
          order
        );
        
        // Split discount into chunks for performance optimization.
        const appliedSplitDiscounts = splitArrayToNChunks(rewards.appliedDiscounts, 10);
        for (const appliedSplitDiscount of appliedSplitDiscounts) {
          await Promise.all(appliedSplitDiscount.map((appliedDiscount) => orderApi.addDiscount(appliedDiscount)));
        }

        // dispatch(setPointBalance(account.balance));
        dispatch(
          fetchDiscountsFulfilled(
            rewards.availableDiscounts,
            rewards.appliedDiscounts,
            rewards.unavailableDiscounts,
            rewards.updatedDiscounts
          )
        );
        dispatch(addCustomerToSale(customer));
      } catch (error) {
        dispatch(fetchDiscountsRejected(error as Error));
      } finally {
        done();
      }
    } else {
      if (!order) {
        dispatch(fetchDiscountsRejected(new Error('Order is not defined')));
      }
      if (!customer) {
        dispatch(fetchDiscountsRejected(new Error('Customer is not defined')));
      }
      done();
    }
  },
});

/** Split array into arrays of n number of chunks. */
function splitArrayToNChunks<T>(array: T[], n: number): Array<T[]> {
  const [...arr] = array;
  const res = [];
  while (arr.length) {
    res.push(arr.splice(0, n));
  }
  return res;
}


export const removeDiscountLogic = createLogic({
  type: REMOVE_DISCOUNT,
  cancelType: CLEAR_DISCOUNTS,

  async process(
    {
      action,
      getState,
      cancelled$,
    }: {
      action: RemoveDiscountAction;
      getState: () => object;
      cancelled$: { subscribe: (cb: () => void) => void };
    },
    dispatch,
    done
  ) {
    let cancelled = false;

    const posContext = (getState() as AppState).context.posContext;
    const customer = (getState() as AppState).customer.customer;

    const discount = action.payload.discount;

    cancelled$.subscribe(() => {
      cancelled = true;
    });

    if (customer) {
      try {
        // Delete the reward
        await orderApi.removeDiscount(discount.uid as string);
        await deleteReward(posContext, discount.metadata.rewardId);
        const updatedOrder = await orderApi.retrieve();

        const rewards = await getRewards(
          posContext,
          customer.referenceId,
          updatedOrder
        );

        if (!cancelled) {
          // Update discounts in redux.
          dispatch(
            fetchDiscountsFulfilled(
              rewards.availableDiscounts,
              rewards.appliedDiscounts,
              rewards.unavailableDiscounts,
              rewards.updatedDiscounts
            )
          );
        }

        dispatch(removeDiscountFulfilled());
      } catch (error) {
        dispatch(removeDiscountRejected(error as Error));
      } finally {
        done();
      }
    } else {
      dispatch(removeDiscountRejected(new Error('Customer is not defined')));
    }
  },
});

export const removeDiscountsLogic = createLogic({
  type: REMOVE_DISCOUNTS,
  cancelType: CLEAR_DISCOUNTS,

  async process(
    {
      action,
      getState,
      cancelled$,
    }: {
      action: RemoveDiscountsAction;
      getState: () => object;
      cancelled$: { subscribe: (cb: () => void) => void };
    },
    dispatch,
    done
  ) {
    let cancelled = false;

    const posContext = (getState() as AppState).context.posContext;
    const customer = (getState() as AppState).customer.customer;

    cancelled$.subscribe(() => {
      cancelled = true;
    });

    if (customer) {
      try {
        for (const discountId of action.payload.discountIds) {
          await orderApi.removeDiscount(discountId);
        }
        const updatedOrder = await orderApi.retrieve();

        const rewards = await getRewards(
          posContext,
          customer.referenceId,
          updatedOrder
        );
        if (!cancelled) {
          // Update discounts in redux.
          dispatch(
            fetchDiscountsFulfilled(
              rewards.availableDiscounts,
              rewards.appliedDiscounts,
              rewards.unavailableDiscounts,
              rewards.updatedDiscounts
            )
          );
        }

        dispatch(removeDiscountsFulfilled());
      } catch (error) {
        dispatch(removeDiscountsRejected(error as Error));
      } finally {
        done();
      }
    } else {
      dispatch(removeDiscountsRejected(new Error('Customer is not defined')));
    }
  },
});
